import * as React from "react";
import classnames from "classnames";
import { FormContext } from "../../Form/Form/Form.js";
import Input from "../../Input/Input/Input.js";
import { isRequired, maxLength } from "../../Util/Util/validations.js";
import FormControlFieldWrapper from "./FormControlWrapper.js";
/* empty css                 */
const isEqualState = (a, b) => a.value === b.value && a.isTouched === b.isTouched && a.isValid === b.isValid && a.readonly === b.readonly;
const _FormControl = class _FormControl extends React.Component {
  constructor() {
    super(...arguments);
    this.state = {
      name: this.props.id,
      errorMessage: this.props.errorMessage
    };
    this.onChange = () => {
      const { isValid, errorMessage } = _FormControl.validate(
        this.props.validations,
        this.props.validationsWithErrorMessages,
        this.props.required,
        this.state.value,
        this.state.errorMessage
      );
      if (this.props.onChange) {
        this.props.onChange({
          ...this.state,
          isValid,
          errorMessage
        });
      }
    };
    this.handleChange = (state) => {
      const value = state.value;
      if (value !== this.state.value) {
        this.setState({ value });
      }
    };
    this.handleBlur = (state) => {
      const value = state.value.trim();
      this.setState({ isTouched: state.isTouched, value }, () => {
        this.props.onBlur?.(this.state);
      });
    };
  }
  static getDerivedStateFromProps(props, state) {
    const { isValid, errorMessage } = _FormControl.validate(
      props.validations,
      props.validationsWithErrorMessages,
      props.required || false,
      state.value || "",
      state.errorMessage,
      props.maxLength
    );
    return {
      isValid,
      errorMessage
    };
  }
  componentDidMount() {
    const initialState = this.props.initialState;
    if (initialState) {
      const { isValid, errorMessage } = _FormControl.validate(
        this.props.validations,
        this.props.validationsWithErrorMessages,
        this.props.required || false,
        initialState.value,
        this.state.errorMessage
      );
      this.setState({
        value: initialState.value || "",
        isTouched: initialState.isTouched !== void 0 && initialState.isTouched,
        isValid,
        errorMessage
      });
      if (initialState.readonly) {
        this.setState({ readonly: true });
      }
    }
  }
  applyInitialState(initialState) {
    const { isValid, errorMessage } = _FormControl.validate(
      this.props.validations,
      this.props.validationsWithErrorMessages,
      this.props.required || false,
      initialState.value,
      this.props.errorMessage
    );
    this.setState({
      value: initialState.value || "",
      isTouched: initialState.isTouched !== void 0 && initialState.isTouched,
      isValid,
      errorMessage
    });
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.initialState && this.props.initialState && !isEqualState(prevProps.initialState, this.props.initialState)) {
      this.applyInitialState(this.props.initialState);
    }
    if (prevState.isTouched !== this.state.isTouched || prevState.isValid !== this.state.isValid || prevState.value !== this.state.value) {
      this.onChange();
    }
  }
  render() {
    const {
      label,
      id,
      type,
      icon,
      errorMessage,
      required,
      placeholder,
      optionalText,
      tooltip,
      initialState,
      disabled,
      className,
      maxLength: maxLength2,
      hideAsteriskSign,
      ...rest
    } = this.props;
    const { isValid, isTouched, value, readonly } = this.state;
    const displayValid = isValid === true && isTouched === true && value !== "" && !readonly;
    const displayError = isValid === false && isTouched === true && !readonly;
    const errorMessageId = `${id}-error-message`;
    const ariaDescribedBy = `${rest["aria-describedby"] || ""} ${errorMessageId}`.trim();
    const inputClasses = classnames("form-control__input", {
      "form-control__input--with-aside": icon,
      _is_valid: displayValid,
      _is_invalid: displayError
    });
    return /* @__PURE__ */ React.createElement(
      FormControlFieldWrapper,
      {
        ...{
          className,
          label,
          required,
          hideAsteriskSign,
          tooltip,
          id,
          optionalText,
          errorMessageId,
          errorMessage: this.state.errorMessage,
          displayError
        }
      },
      /* @__PURE__ */ React.createElement(
        Input,
        {
          "aria-invalid": !isValid,
          "aria-required": required,
          "aria-describedby": ariaDescribedBy,
          id,
          className: inputClasses,
          "data-t": this.props["data-t"],
          type,
          initialValue: initialState && initialState.value || "",
          placeholder,
          onChange: this.handleChange,
          onBlur: this.handleBlur,
          disabled: disabled || readonly,
          maxLength: maxLength2
        }
      ),
      icon && /* @__PURE__ */ React.createElement("div", { className: "form-control__aside" }, icon)
    );
  }
};
_FormControl.defaultProps = {
  type: "text",
  hideAsteriskSign: false,
  required: true,
  validations: [],
  initialState: {
    value: "",
    isTouched: false
  },
  disabled: false
};
_FormControl.validate = (validations, validationsWithErrorMessages = [], required, value, initialErrorMessage, maxLengthValue) => {
  let errorMessage = initialErrorMessage ?? "";
  if (!required && value.length === 0) {
    return { isValid: true, errorMessage };
  }
  if (required) {
    if (!isRequired(value)) {
      return { isValid: false, errorMessage };
    }
  }
  if (maxLengthValue && !maxLength(maxLengthValue)(value)) {
    return { isValid: false, errorMessage };
  }
  const validationsAreMet = validations.reduce(
    (isValid, validator) => validator(value) && isValid,
    true
  );
  let validationsWithErrorMessagesAreMet = true;
  for (const validatorWithErrorMessage of validationsWithErrorMessages) {
    const isValid = validatorWithErrorMessage.validator(value);
    validationsWithErrorMessagesAreMet = isValid;
    if (!isValid) {
      errorMessage = validatorWithErrorMessage.errorMessage;
      break;
    }
  }
  return { isValid: validationsAreMet && validationsWithErrorMessagesAreMet, errorMessage };
};
let FormControl = _FormControl;
const ConnectedFormControl = ({
  onBlur,
  onChange,
  ...rest
}) => /* @__PURE__ */ React.createElement(FormContext.Consumer, null, (context = {}) => /* @__PURE__ */ React.createElement(
  FormControl,
  {
    ...rest,
    onBlur: context.onBlur || onBlur,
    onChange: context.onChange || onChange,
    initialState: context.getInitialState && context.getInitialState(rest.id) || rest.initialState && rest.initialState
  }
));
ConnectedFormControl.defaultProps = {
  ...FormControl.defaultProps
};
export {
  FormControl,
  ConnectedFormControl as default
};
//# sourceMappingURL=FormControl.js.map
